import {useAtomValue} from 'jotai';
import {ChangeEvent, useCallback, useMemo} from 'react';
import styled from 'styled-components';
import {Checkbox, CheckboxItem, CheckboxLabel} from '@/components/Checkbox';
import {Typography} from '@/components/Typography';
import {AnalysisItem} from '@/map/@types';
import {compareAreaItemsAtom} from '@/map/atom';
import {AreaOverlayType} from '@/map/enums';
import {panToBoundCircle, panToBoundsPolygon} from '@/map/modules';
import {distanceToString, getKm2} from '@/map/utils';
import {Flexbox, FlexFullGrow} from '@/styled-components';
import {palette} from '@/styles';
import {FocusIcon} from './StyledComponents';

interface IProps {
  item: AnalysisItem;
  map: naver.maps.Map;
  onChange(id: string, checked: boolean): void;
}

const titlePrefix = {
  [AreaOverlayType.CIRCLE]: '반경',
  [AreaOverlayType.POLYGON]: '면적',
  [AreaOverlayType.EMD]: '',
};

export const ListItem = ({item, map, onChange}: IProps) => {
  const selected = useAtomValue(compareAreaItemsAtom);
  const selectedIdx = selected.findIndex((i) => i === String(item.id));

  const onFocus = () => {
    const margin = {left: 500, right: 350};

    if (item.polygon?.paths) {
      panToBoundsPolygon(item.polygon.paths, map, margin);
    } else if (item.circle?.center && item.circle.radius) {
      panToBoundCircle(item.circle as any, map, margin);
    }
  };

  const title = useMemo(() => {
    if (!item) {
      return '분석지역';
    }

    const prefix = titlePrefix[item.type];

    return `${prefix} ${
      item.polygon?.m2
        ? `${getKm2(item.polygon.m2)}k㎡`
        : item.circle?.radius
        ? distanceToString(item.circle.radius, {roundingMode: 'ceil'})
        : ''
    }`;
  }, [item]);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const {checked, name} = e.target;
      onChange(name, checked);
    },
    [onChange]
  );

  return (
    <Root>
      <FocusIcon onClick={onFocus} />
      <Typography variant="medium14" color={palette.textSecondary}>
        {title}
      </Typography>
      <FlexFullGrow />
      <CheckboxItem>
        {selectedIdx >= 0 && (
          <CheckboxLabel>
            <Typography variant="medium12" color={palette.primary400}>
              비교 상권 {selectedIdx + 1}
            </Typography>
          </CheckboxLabel>
        )}
        <Checkbox
          name={item.id}
          checked={selectedIdx >= 0}
          onChange={handleChange}
        />
      </CheckboxItem>
    </Root>
  );
};

const Root = styled(Flexbox)`
  width: 100%;
  height: 42px;
  padding: 0 16px;
  gap: 8px;
  font-weight: 500;
`;
